import { ProfileUsers } from '../api/enumerations';

/**
 * RBAC de navegação (REAL-3099).
 *
 * Mapa de visibilidade por sessão/aba → papéis permitidos. `admin` é implícito
 * em tudo (bypass em `canSee`). Papéis: BANK_RESPONSAVEL_TECNICO=14, ADMIN=3, FINANCEIRO=8,
 * JURIDICO=17, CS=18. Lista vazia = só admin.
 *
 * Camada B do handoff: o backend NÃO bloqueia estas sessões (endpoints
 * compartilhados), então esconder aqui é a única barreira.
 */
export type AccessKey =
  | 'home'
  | 'archive'
  | 'dashboard'
  | 'preflight'
  | 'finance'
  | 'userPanel'
  | 'notifications'
  | 'forms'
  | 'hive'
  | 'hiveDashboard'
  | 'hivePedidos'
  | 'hiveCredenciados'
  | 'hiveEditais'
  | 'noticeConfig'
  | 'noticeLicensed'
  | 'noticeRequests'
  | 'noticeLimits'
  | 'analiseJuridica'
  | 'analiseEngenharia'
  | 'analysisQueueJuridico'
  | 'analysisQueueEngenharia'
  | 'analysisQueueContrato';

const {
  BANK_GESTOR_CREDENCIADO,
  BANK_GESTOR_EDITAIS,
  BANK_RESPONSAVEL_TECNICO,
  ENGINEER,
  FINANCEIRO,
  JURIDICO,
  CS,
} = ProfileUsers;

export const SESSION_ROLES: Record<AccessKey, number[]> = {
  // Sessões do menu
  home: [BANK_RESPONSAVEL_TECNICO],
  archive: [BANK_RESPONSAVEL_TECNICO],
  dashboard: [], // só admin
  preflight: [BANK_RESPONSAVEL_TECNICO],
  finance: [FINANCEIRO],
  userPanel: [], // só admin
  notifications: [BANK_RESPONSAVEL_TECNICO],
  forms: [BANK_RESPONSAVEL_TECNICO],
  // The Hive — bank managers and RealPrice engineers were added for the
  // PROT-2950 screens (pair limits, waitlist control, analysis queues);
  // the /the-hive PrivateRoute allowedRoles list in App.tsx must stay in
  // sync with `hive`, or these grants become unreachable deep-links.
  hive: [
    BANK_RESPONSAVEL_TECNICO,
    FINANCEIRO,
    JURIDICO,
    CS,
    BANK_GESTOR_EDITAIS,
    BANK_GESTOR_CREDENCIADO,
    ENGINEER,
  ],
  hiveDashboard: [FINANCEIRO, CS],
  hivePedidos: [BANK_RESPONSAVEL_TECNICO],
  hiveCredenciados: [BANK_RESPONSAVEL_TECNICO],
  hiveEditais: [
    BANK_RESPONSAVEL_TECNICO,
    JURIDICO,
    BANK_GESTOR_EDITAIS,
    BANK_GESTOR_CREDENCIADO,
    ENGINEER,
  ],
  // Sub-abas de um edital
  noticeConfig: [], // só admin
  noticeLicensed: [], // só admin
  noticeRequests: [
    BANK_RESPONSAVEL_TECNICO,
    JURIDICO,
    BANK_GESTOR_EDITAIS,
    BANK_GESTOR_CREDENCIADO,
    ENGINEER,
  ],
  // Pair limits + waitlist control (PROT-2950), mirroring the backend
  // policy: bank_admin/bank_gestor_editais on banks, engenheiro/admin on
  // RealPrice. BANK_RESPONSAVEL_TECNICO was removed — the backend refuses
  // both the GET and the PATCH for that role, so the tab was a dead end.
  noticeLimits: [BANK_GESTOR_EDITAIS, ENGINEER],
  // Abas do detalhe do pedido (aplicado onde as abas existem — REAL-3102)
  analiseJuridica: [JURIDICO],
  analiseEngenharia: [BANK_RESPONSAVEL_TECNICO],
  // Analysis priority queues (PROT-2950), mirroring the backend policy:
  // bank managers see every queue (the bank juridico chip only the legal
  // one); RealPrice juridico -> legal, engenheiro -> engineering, both ->
  // contract; admin sees everything.
  analysisQueueJuridico: [
    JURIDICO,
    BANK_GESTOR_EDITAIS,
    BANK_GESTOR_CREDENCIADO,
  ],
  analysisQueueEngenharia: [
    ENGINEER,
    BANK_GESTOR_EDITAIS,
    BANK_GESTOR_CREDENCIADO,
  ],
  analysisQueueContrato: [
    ENGINEER,
    JURIDICO,
    BANK_GESTOR_EDITAIS,
    BANK_GESTOR_CREDENCIADO,
  ],
};

/**
 * Papéis com acesso total (bypassam o RBAC): admin da plataforma (3) e
 * admin do banco (bank_admin, 10) — tratado igual ao admin.
 */
export const isFullAccessRole = (role: number | null | undefined): boolean =>
  role === ProfileUsers.ADMIN || role === ProfileUsers.BANK_ADMIN;

/** Acesso total vê tudo; senão, precisa estar na lista da key. `null` → sem acesso. */
export const canSee = (key: AccessKey, role: number | null): boolean => {
  if (role == null) return false;
  if (isFullAccessRole(role)) return true;
  return SESSION_ROLES[key].includes(role);
};
